// =============================
// File: src/components/admin/CategoryManager.jsx
// =============================
import React, { useState, useEffect } from 'react';
import {
  ArrowLeft,
  Plus,
  Edit,
  Trash2,
  FolderOpen,
  Save,
  X,
  Search
} from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

function CategoryManager({ categories, onBack, onUpdate }) {
  const [localCategories, setLocalCategories] = useState(categories);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingCategory, setEditingCategory] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    icon: 'FileText',
    role_access: 'all',
    order_index: 0
  });

  useEffect(() => {
    setLocalCategories(categories);
  }, [categories]);

  // Auto-generate slug from name
  useEffect(() => {
    if (!editingCategory && formData.name) {
      const slug = formData.name
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .trim();
      setFormData(prev => ({ ...prev, slug }));
    }
  }, [formData.name, editingCategory]);

  function handleChange(e) {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  }

  function resetForm() {
    setFormData({
      name: '',
      slug: '',
      description: '',
      icon: 'FileText',
      role_access: 'all',
      order_index: 0
    });
    setEditingCategory(null);
  }

  function handleEdit(category) {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      slug: category.slug,
      description: category.description || '',
      icon: category.icon || 'FileText',
      role_access: category.role_access,
      order_index: category.order_index
    });
    setShowCreateModal(true);
  }

  async function handleSave() {
    if (!formData.name.trim()) {
      alert('El nombre es obligatorio');
      return;
    }
    if (!formData.slug.trim()) {
      alert('El slug es obligatorio');
      return;
    }

    try {
      if (editingCategory) {
        // Update existing category
        await axios.put(`${API_URL}/api/docs/categories/${editingCategory.id}`, formData);
        alert('Categoría actualizada exitosamente');
      } else {
        // Create new category
        await axios.post(`${API_URL}/api/docs/categories`, formData);
        alert('Categoría creada exitosamente');
      }

      setShowCreateModal(false);
      resetForm();
      onUpdate();
    } catch (error) {
      console.error('Error saving category:', error);
      alert('Error al guardar la categoría');
    }
  }

  async function handleDelete(categoryId) {
    const category = localCategories.find(c => c.id === categoryId);
    
    if (category.doc_count > 0) {
      alert(`No se puede eliminar esta categoría porque tiene ${category.doc_count} documento(s) asociado(s). Primero elimina o mueve los documentos.`);
      return;
    }

    if (!window.confirm('¿Estás seguro de eliminar esta categoría?')) return;

    try {
      await axios.delete(`${API_URL}/api/docs/categories/${categoryId}`);
      alert('Categoría eliminada exitosamente');
      onUpdate();
    } catch (error) {
      console.error('Error deleting category:', error);
      alert('Error al eliminar la categoría');
    }
  }

  const filteredCategories = localCategories.filter(cat =>
    cat.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    cat.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Common Lucide icons for categories
  const popularIcons = [
    'FileText', 'BookOpen', 'User', 'Users', 'Package', 'Truck',
    'MapPin', 'Settings', 'HelpCircle', 'Info', 'Bell', 'Star',
    'Heart', 'Shield', 'Lock', 'Key', 'Mail', 'Phone', 'Map',
    'Calendar', 'Clock', 'CreditCard', 'DollarSign', 'ShoppingCart',
    'Tag', 'Gift', 'Award', 'Zap', 'TrendingUp', 'BarChart'
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-brand-primary to-pink-600 shadow-md">
        <div className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={onBack}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-white" />
              </button>
              <div className="flex items-center gap-3">
                <FolderOpen className="w-8 h-8 text-white" />
                <div>
                  <h1 className="text-2xl font-extrabold text-white font-montserrat">
                    Gestión de Categorías
                  </h1>
                  <p className="text-sm text-white/80 font-montserrat">
                    {localCategories.length} categoría(s)
                  </p>
                </div>
              </div>
            </div>

            <button
              onClick={() => {
                resetForm();
                setShowCreateModal(true);
              }}
              className="px-4 py-2 bg-white text-brand-primary hover:bg-white/90 rounded-lg font-semibold font-montserrat transition-all flex items-center gap-2 shadow-lg"
            >
              <Plus className="w-5 h-5" />
              <span className="hidden sm:inline">Nueva Categoría</span>
            </button>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              placeholder="Buscar categorías..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent font-montserrat"
            />
          </div>
        </div>

        {/* Categories Grid */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filteredCategories.map((category) => {
            const IconComponent = LucideIcons[category.icon] || FolderOpen;
            
            return (
              <div
                key={category.id}
                className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 hover:shadow-md transition-all"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-brand-primary to-brand-gold flex items-center justify-center shadow-md">
                      <IconComponent className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-slate-900 font-montserrat truncate">
                        {category.name}
                      </h3>
                      <p className="text-xs text-slate-500 font-montserrat">
                        {category.doc_count} doc(s)
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={() => handleEdit(category)}
                      className="p-2 hover:bg-blue-100 rounded-lg transition-colors"
                      title="Editar"
                    >
                      <Edit className="w-4 h-4 text-blue-600" />
                    </button>
                    <button
                      onClick={() => handleDelete(category.id)}
                      className="p-2 hover:bg-red-100 rounded-lg transition-colors"
                      title="Eliminar"
                      disabled={category.doc_count > 0}
                    >
                      <Trash2 className={`w-4 h-4 ${category.doc_count > 0 ? 'text-slate-300' : 'text-red-600'}`} />
                    </button>
                  </div>
                </div>

                {category.description && (
                  <p className="text-sm text-slate-600 font-montserrat line-clamp-2 mb-3">
                    {category.description}
                  </p>
                )}

                <div className="flex items-center gap-2 text-xs">
                  <span className="px-2 py-1 bg-slate-100 text-slate-700 rounded font-montserrat font-semibold">
                    {getRoleLabel(category.role_access)}
                  </span>
                  <span className="px-2 py-1 bg-slate-100 text-slate-700 rounded font-montserrat">
                    Orden: {category.order_index}
                  </span>
                </div>
              </div>
            );
          })}

          {filteredCategories.length === 0 && (
            <div className="col-span-full text-center py-12">
              <FolderOpen className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 font-montserrat mb-2">
                No hay categorías
              </h3>
              <p className="text-slate-600 font-montserrat mb-6">
                {searchQuery ? 'No se encontraron resultados' : 'Comienza creando tu primera categoría'}
              </p>
              {!searchQuery && (
                <button
                  onClick={() => {
                    resetForm();
                    setShowCreateModal(true);
                  }}
                  className="px-6 py-3 bg-brand-primary text-white rounded-lg font-semibold font-montserrat hover:bg-brand-primary/90 transition-colors"
                >
                  Crear Primera Categoría
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
          <div className="w-full max-w-2xl rounded-2xl bg-white shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 z-10">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-extrabold text-slate-900 font-montserrat">
                  {editingCategory ? 'Editar Categoría' : 'Nueva Categoría'}
                </h2>
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    resetForm();
                  }}
                  className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-slate-400" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Name */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 font-montserrat mb-2">
                  Nombre de la Categoría *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Ej: Autenticación"
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent font-montserrat"
                />
              </div>

              {/* Slug */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 font-montserrat mb-2">
                  Slug (URL) *
                </label>
                <input
                  type="text"
                  name="slug"
                  value={formData.slug}
                  onChange={handleChange}
                  placeholder="autenticacion"
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent font-montserrat"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 font-montserrat mb-2">
                  Descripción (opcional)
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows={3}
                  placeholder="Describe el propósito de esta categoría..."
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent font-montserrat resize-none"
                />
              </div>

              {/* Icon Selection */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 font-montserrat mb-2">
                  Icono
                </label>
                <div className="grid grid-cols-6 sm:grid-cols-8 gap-2 max-h-48 overflow-y-auto p-2 border border-slate-300 rounded-lg">
                  {popularIcons.map((iconName) => {
                    const IconComponent = LucideIcons[iconName];
                    if (!IconComponent) return null;

                    return (
                      <button
                        key={iconName}
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, icon: iconName }))}
                        className={`
                          p-3 rounded-lg border-2 transition-all flex items-center justify-center
                          ${formData.icon === iconName
                            ? 'border-brand-primary bg-brand-primary/10'
                            : 'border-slate-200 hover:border-brand-primary/50 hover:bg-slate-50'
                          }
                        `}
                        title={iconName}
                      >
                        <IconComponent className={`w-5 h-5 ${formData.icon === iconName ? 'text-brand-primary' : 'text-slate-600'}`} />
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-slate-500 font-montserrat mt-2">
                  Icono seleccionado: <span className="font-semibold">{formData.icon}</span>
                </p>
              </div>

              {/* Role Access */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 font-montserrat mb-2">
                  Acceso por Rol *
                </label>
                <select
                  name="role_access"
                  value={formData.role_access}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent font-montserrat"
                >
                  <option value="all">Todos los roles</option>
                  <option value="customer">Solo Usuarios</option>
                  <option value="driver">Solo Conductores</option>
                  <option value="provider">Solo Proveedores</option>
                  <option value="admin">Solo Administradores</option>
                </select>
              </div>

              {/* Order Index */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 font-montserrat mb-2">
                  Orden de visualización
                </label>
                <input
                  type="number"
                  name="order_index"
                  value={formData.order_index}
                  onChange={handleChange}
                  min="0"
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent font-montserrat"
                />
                <p className="text-xs text-slate-500 font-montserrat mt-1">
                  Las categorías se ordenan de menor a mayor (0 aparece primero)
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="sticky bottom-0 bg-white border-t border-slate-200 px-6 py-4 flex items-center justify-end gap-3">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  resetForm();
                }}
                className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg font-semibold font-montserrat hover:bg-slate-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleSave}
                className="px-4 py-2 bg-brand-primary text-white rounded-lg font-semibold font-montserrat hover:bg-brand-primary/90 transition-colors flex items-center gap-2"
              >
                <Save className="w-4 h-4" />
                {editingCategory ? 'Actualizar' : 'Crear'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function getRoleLabel(role) {
  const labels = {
    customer: 'Usuarios',
    driver: 'Conductores',
    provider: 'Proveedores',
    admin: 'Administradores',
    all: 'Todos'
  };
  return labels[role] || role;
}

export default CategoryManager;